import { useTrail, useSpring, a, UseSpringProps, UseTrailProps } from '@react-spring/web';
import clsx from 'clsx';
import React from 'react';
import { useInView } from 'react-intersection-observer';

export const Trail = ({
  open = true,
  children,
  className,
  trailProps,
}: {
  open?: boolean;
  children: React.ReactNode;
  className?: string;
  trailProps?: Partial<UseTrailProps>;
}) => {
  const items = React.Children.toArray(children);
  const trail = useTrail(items.length, {
    config: { mass: 5, tension: 2000, friction: 200 },
    opacity: open ? 1 : 0,
    x: open ? 0 : 20,
    height: open ? 40 : 0,
    from: { opacity: 0, x: 20, height: 0 },
    ...trailProps,
  });
  return (
    <>
      {trail.map((style, index) => (
        <a.div key={index} className={clsx(className)} style={style}>
          {items[index]}
        </a.div>
      ))}
    </>
  );
};

export const Fade = ({
  children,
  style,
  onFinishFade,
  type,
  springProps,
  ...rest
}: {
  children?: React.ReactNode;
  onFinishFade?: () => void;
  type: 'fade-out' | 'fade-in';
  springProps?: Partial<UseSpringProps>;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const spring = useSpring({
    from: { opacity: type === 'fade-out' ? 1 : 0 },
    to: { opacity: type === 'fade-out' ? 0 : 1 },
    onRest: onFinishFade,
    ...springProps,
  });

  return (
    <a.div style={{ ...spring, ...style }} {...rest}>
      {children}
    </a.div>
  );
};

export const FadeOnScroll = ({
  children,
  height,
}: {
  children: React.ReactNode;
  height: string;
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '-50px 0px',
    // rootMargin: `-${height / 2}px`,
    delay: 300,
  });
  const [styles, api] = useSpring(() => ({
    opacity: 0,
    y: 80,
  }));
  React.useEffect(() => {
    if (!inView) {
      api.stop();
      return;
    }
    api.start({
      to: { opacity: 1, y: 0 },
      config: {
        duration: 700,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (!inView) return <div className="w-full" style={{ height }} ref={ref} />;
  return (
    <a.div className="w-full" style={{ height, ...styles }}>
      {children}
    </a.div>
  );
};
